const HOMEPAGE_ORIGIN_STORAGE_KEY = "mudify:home-vehicle-shop-origin";
class HomeVehicleShop extends window.HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.scrollFrame = null;
    this.currentPage = 0;
    this.mobileModalCloseTimer = null;
    this.goToPageTimer = null;
    this.isProgrammaticScroll = false;
    this.sectionRoot = this.closest(".vehicle-shop-section") || this;
    this.scroller = this.sectionRoot.querySelector("[data-vehicle-scroller]");
    this.cards = Array.from(this.sectionRoot.querySelectorAll("[data-vehicle-track] > *"));
    this.prevButtons = Array.from(this.sectionRoot.querySelectorAll("[data-prev]"));
    this.nextButtons = Array.from(this.sectionRoot.querySelectorAll("[data-next]"));
    this.pagination = this.sectionRoot.querySelector("[data-pagination]");
    this.navShells = Array.from(this.sectionRoot.querySelectorAll("[data-nav-shell]"));
    this.categoryDropdown = this.sectionRoot.querySelector("[data-category-dropdown]");
    this.categoryToggle = this.sectionRoot.querySelector("[data-category-toggle]");
    this.categoryMenu = this.sectionRoot.querySelector("[data-category-menu]");
    this.categoryBackdrop = this.sectionRoot.querySelector("[data-category-backdrop]");
    this.categoryOptions = Array.from(this.sectionRoot.querySelectorAll("[data-category-option]"));
    this.activeCategoryLabel = this.sectionRoot.querySelector("[data-active-category-label]");
    this.vehicleCards = Array.from(this.sectionRoot.querySelectorAll("[data-vehicle-card]"));
    this.mobileModal = this.sectionRoot.querySelector("[data-mobile-vehicle-modal]");
    this.mobileModalSheet = this.sectionRoot.querySelector("[data-mobile-vehicle-modal-sheet]");
    this.mobileModalTitle = this.sectionRoot.querySelector("[data-mobile-vehicle-modal-title]");
    this.mobileModalCategory = this.sectionRoot.querySelector("[data-mobile-vehicle-modal-category]");
    this.mobileModalMake = this.sectionRoot.querySelector("[data-mobile-vehicle-modal-make]");
    this.mobileModalBrandLogo = this.sectionRoot.querySelector("[data-mobile-vehicle-modal-brand-logo]");
    this.mobileModalItems = this.sectionRoot.querySelector("[data-mobile-vehicle-modal-items]");
    this.mobileModalCloseButtons = Array.from(
      this.sectionRoot.querySelectorAll("[data-mobile-vehicle-modal-close]")
    );
    this.dataElement = this.sectionRoot.querySelector("[data-vehicle-shop-data]");
    this.shopData = this.parseShopData();
    this.activeCategoryKey = this.getInitialCategoryKey();
    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
    this.handleHomeVehicleShopOriginClick = this.handleHomeVehicleShopOriginClick.bind(this);
    this.bindCategoryDropdown();
    this.bindVehicleCards();
    this.bindMobileModal();
    document.addEventListener("click", this.handleDocumentClick);
    document.addEventListener("keydown", this.handleDocumentKeydown);
    this.sectionRoot.addEventListener("click", this.handleHomeVehicleShopOriginClick);
    this.applyCategorySelection(this.activeCategoryKey);
    if (!this.scroller || !this.cards.length) return;
    this.updateScrollerPadding();
    this.bindNavEvents();
    this.buildPages();
    this.updateButtons();
    this.updatePagination();
    this.scroller.addEventListener("scroll", this.handleScroll, { passive: true });
    window.addEventListener("resize", this.handleResize);
  }
  disconnectedCallback() {
    var _a;
    (_a = this.scroller) == null ? void 0 : _a.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener("keydown", this.handleDocumentKeydown);
    this.sectionRoot.removeEventListener("click", this.handleHomeVehicleShopOriginClick);
    if (this.scrollFrame) {
      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
    }
    if (this.mobileModalCloseTimer) {
      window.clearTimeout(this.mobileModalCloseTimer);
      this.mobileModalCloseTimer = null;
    }
    if (this.goToPageTimer) {
      window.clearTimeout(this.goToPageTimer);
      this.goToPageTimer = null;
    }
  }
  parseShopData() {
    if (!this.dataElement) {
      return { linksets: {} };
    }
    try {
      return JSON.parse(this.dataElement.textContent || "{}");
    } catch (error) {
      console.warn("HomeVehicleShop: failed to parse vehicle shop payload", error);
      return { linksets: {} };
    }
  }
  bindNavEvents() {
    this.prevButtons.forEach((button) => {
      button.addEventListener("click", () => this.goToPage(this.currentPage - 1));
    });
    this.nextButtons.forEach((button) => {
      button.addEventListener("click", () => this.goToPage(this.currentPage + 1));
    });
  }
  bindCategoryDropdown() {
    var _a;
    if (!this.categoryDropdown || !this.categoryToggle || !this.categoryMenu) return;
    this.categoryToggle.addEventListener("click", () => {
      this.setCategoryMenuState(this.categoryMenu.hidden);
    });
    this.categoryOptions.forEach((option) => {
      option.addEventListener("click", () => this.handleCategorySelection(option));
    });
    (_a = this.categoryBackdrop) == null ? void 0 : _a.addEventListener("click", () => {
      this.setCategoryMenuState(false);
    });
  }
  bindVehicleCards() {
    this.vehicleCards.forEach((card) => {
      card.addEventListener("click", (event) => this.handleVehicleCardClick(event, card));
      card.addEventListener("keydown", (event) => this.handleVehicleCardKeydown(event, card));
      card.addEventListener("mouseenter", () => this.handleVehicleCardMouseEnter(card));
      card.addEventListener("mouseleave", () => this.handleVehicleCardMouseLeave(card));
    });
  }
  handleVehicleCardMouseEnter(card) {
    if (!this.usesVehicleCardHover() || card.getAttribute("role") !== "button") return;
    this.closeVehicleCards(card);
    this.setVehicleCardState(card, true);
  }
  handleVehicleCardMouseLeave(card) {
    if (!this.usesVehicleCardHover()) return;
    this.setVehicleCardState(card, false);
  }
  parseVehicleFromHref(href) {
    if (!href) return null;
    let parsedUrl;
    try {
      parsedUrl = new URL(href, window.location.origin);
    } catch (error) {
      return null;
    }
    const rawSearch = parsedUrl.search || "";
    if (rawSearch.length < 2) {
      return null;
    }
    const segments = rawSearch.slice(1).split("/").map((segment) => {
      try {
        return window.decodeURIComponent(segment || "").trim();
      } catch (error) {
        return (segment || "").trim();
      }
    }).filter(Boolean);
    if (!segments.length || segments.length % 2 !== 0) {
      return null;
    }
    const supportedFields = /* @__PURE__ */ new Set(["Year", "Make", "Model", "Generation"]);
    const vehicle = {};
    for (let index = 0; index < segments.length; index += 2) {
      const field = segments[index];
      const value = segments[index + 1];
      if (!supportedFields.has(field) || !value) {
        continue;
      }
      vehicle[field] = value;
    }
    if (vehicle.Year && vehicle.Make && vehicle.Model) {
      return {
        fields: ["Year", "Make", "Model"],
        value: vehicle
      };
    }
    if (vehicle.Make && vehicle.Model && vehicle.Generation) {
      return {
        fields: ["Make", "Model", "Generation"],
        value: vehicle
      };
    }
    return null;
  }
  syncConvermaxVehicleFromHref(href) {
    const parsedVehicle = this.parseVehicleFromHref(href);
    if (!parsedVehicle || !window.Convermax || typeof window.Convermax.setVehicle !== "function") {
      return false;
    }
    window.Convermax.setVehicle(parsedVehicle.value);
    return true;
  }
  bindMobileModal() {
    this.mobileModalCloseButtons.forEach((button) => {
      button.addEventListener("click", () => this.closeMobileVehicleModal({ animate: true }));
    });
  }
  usesDesktopCarousel() {
    return window.matchMedia("(min-width: 1024px)").matches;
  }
  usesMobileVehicleModal() {
    return !this.usesDesktopCarousel();
  }
  usesVehicleCardHover() {
    return this.usesDesktopCarousel() && window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  }
  getInitialCategoryKey() {
    var _a;
    return ((_a = this.dataset.activeCategoryKey) == null ? void 0 : _a.trim()) || null;
  }
  handleCategorySelection(option) {
    var _a;
    const categoryKey = (_a = option.dataset.categoryKey) == null ? void 0 : _a.trim();
    if (!categoryKey) return;
    this.activeCategoryKey = categoryKey;
    this.applyCategorySelection(categoryKey, option.dataset.categoryLabel);
    this.setCategoryMenuState(false);
  }
  applyCategorySelection(categoryKey, categoryLabel) {
    if (categoryKey) {
      this.dataset.activeCategoryKey = categoryKey;
    }
    this.categoryOptions.forEach((option) => {
      const isActive = option.dataset.categoryKey === categoryKey;
      option.setAttribute("aria-pressed", isActive ? "true" : "false");
      if (isActive && this.activeCategoryLabel) {
        this.activeCategoryLabel.textContent = option.dataset.categoryLabel || categoryLabel || "";
      }
    });
    if (this.activeCategoryLabel && categoryLabel && !this.activeCategoryLabel.textContent) {
      this.activeCategoryLabel.textContent = categoryLabel;
    }
    this.updateVehicleContent(categoryKey);
    this.closeVehicleCards();
    this.closeMobileVehicleModal();
  }
  updateVehicleContent(categoryKey) {
    this.vehicleCards.forEach((card) => {
      var _a;
      const vehicleKey = (_a = card.dataset.vehicleKey) == null ? void 0 : _a.trim();
      const linkset = this.getLinkset(categoryKey, vehicleKey);
      this.applyLinksetToCard(card, linkset);
    });
  }
  getLinkset(categoryKey, vehicleKey) {
    var _a;
    if (!categoryKey || !vehicleKey) return null;
    return ((_a = this.shopData.linksets) == null ? void 0 : _a[`${categoryKey}::${vehicleKey}`]) || null;
  }
  applyLinksetToCard(card, linkset) {
    const panel = card.querySelector("[data-vehicle-panel]");
    const panelItems = card.querySelector("[data-vehicle-panel-items]");
    const link = card.querySelector("[data-vehicle-link]");
    const vehicleTitle = card.dataset.vehicleTitle || "";
    const hasExplicitCardUrl = linkset && Object.prototype.hasOwnProperty.call(linkset, "cardUrl");
    const items = Array.isArray(linkset == null ? void 0 : linkset.items) ? linkset.items.filter((item) => (item == null ? void 0 : item.label) && (item == null ? void 0 : item.url)) : [];
    const rawCardUrl = hasExplicitCardUrl ? linkset.cardUrl : card.dataset.defaultUrl || "";
    const cardUrl = String(rawCardUrl || "").trim();
    const hasPanel = items.length > 0;
    const vehicleMake = String((linkset == null ? void 0 : linkset.make) || card.dataset.vehicleMake || "").trim();
    const brandLogoSrc = String(
      (linkset == null ? void 0 : linkset.brandLogoSrc) || card.dataset.vehicleBrandLogoSrc || ""
    ).trim();
    const brandLogoAlt = String(
      (linkset == null ? void 0 : linkset.brandLogoAlt) || card.dataset.vehicleBrandLogoAlt || vehicleMake || vehicleTitle
    ).trim();
    if (panelItems) {
      panelItems.innerHTML = hasPanel ? items.map((item) => this.renderGenerationItem(item)).join("") : "";
    }
    card.dataset.vehicleMake = vehicleMake;
    card.dataset.vehicleBrandLogoSrc = brandLogoSrc;
    card.dataset.vehicleBrandLogoAlt = brandLogoAlt;
    if (link) {
      if (hasPanel) {
        link.removeAttribute("href");
        link.hidden = false;
        link.removeAttribute("aria-hidden");
        link.setAttribute("role", "button");
        link.setAttribute("tabindex", "0");
        link.setAttribute("aria-label", `Open options for ${vehicleTitle}`.trim());
      } else if (cardUrl) {
        link.href = cardUrl;
        link.hidden = false;
        link.removeAttribute("aria-hidden");
        link.removeAttribute("tabindex");
        link.removeAttribute("role");
        link.setAttribute("aria-label", `Shop ${vehicleTitle}`.trim());
      } else {
        link.removeAttribute("href");
        link.hidden = true;
        link.setAttribute("aria-hidden", "true");
        link.setAttribute("tabindex", "-1");
        link.removeAttribute("role");
        link.setAttribute("aria-label", `Shop ${vehicleTitle}`.trim());
      }
    }
    if (!hasPanel && panel) {
      panel.hidden = true;
    }
    this.setCardInteractionState(card, hasPanel);
  }
  setCardInteractionState(card, hasPanel) {
    const panelId = card.dataset.panelId;
    card.classList.toggle("cursor-pointer", hasPanel);
    card.classList.remove("is-active");
    if (hasPanel) {
      card.setAttribute("tabindex", "0");
      card.setAttribute("role", "button");
      card.setAttribute("aria-expanded", "false");
      if (panelId) {
        card.setAttribute("aria-controls", panelId);
      }
      return;
    }
    card.removeAttribute("tabindex");
    card.removeAttribute("role");
    card.removeAttribute("aria-expanded");
    card.removeAttribute("aria-controls");
  }
  renderGenerationItem(item) {
    const label = this.escapeHtml(item.label || "");
    const subtext = item.subtext ? `<span class="mx-1 font-dinpro font-bold text-[14px] text-[#000000]">/</span><span class="font-inter text-[12px] uppercase font-medium tracking-normal text-[#000000]">${this.escapeHtml(String(item.subtext).toUpperCase())}</span>` : "";
    const url = this.escapeHtml(item.url || "");
    const homeVehicleShopOriginAttribute = item.homeVehicleShopOrigin ? ' data-home-vehicle-shop-origin="true"' : "";
    return `<a href="${url}" data-generation-link${homeVehicleShopOriginAttribute} class="flex w-full items-center justify-between gap-3 rounded-[2px] px-3 py-3 text-[#4F4F4F] transition"><span class="min-w-0 font-dinpro font-bold text-[14px] uppercase leading-none tracking-[0.02em] text-[#000000]">${label}${subtext}</span><span class="shrink-0 text-[#6B6B6B]"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" class="h-5 w-5"><path d="M5.75 10H14.25M14.25 10L10.5 6.25M14.25 10L10.5 13.75" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"></path></svg></span></a>`;
  }
  renderMobileGenerationItem(item) {
    const label = this.escapeHtml(item.label || "");
    const subtext = item.subtext ? `<span class="ml-1 font-dinpro text-[17px] font-bold uppercase tracking-normal text-[#2F2F2F]">/ ${this.escapeHtml(String(item.subtext).toUpperCase())}</span>` : "";
    const url = this.escapeHtml(item.url || "");
    const homeVehicleShopOriginAttribute = item.homeVehicleShopOrigin ? ' data-home-vehicle-shop-origin="true"' : "";
    return `<a href="${url}" data-generation-link${homeVehicleShopOriginAttribute} class="flex items-center justify-between gap-4 py-3.5 text-[#303030]"><span class="min-w-0 font-dinpro text-[24px] font-bold uppercase leading-none tracking-normal text-[#303030]">${label}${subtext}</span><span class="shrink-0 text-[#777777]"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" class="h-5 w-5"><path d="M5.75 10H14.25M14.25 10L10.5 6.25M14.25 10L10.5 13.75" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"></path></svg></span></a>`;
  }
  escapeHtml(value) {
    return String(value).replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;").replaceAll("'", "&#39;");
  }
  handleVehicleCardClick(event, card) {
    if (card.getAttribute("role") !== "button") return;
    const target = event.target instanceof window.Element ? event.target : null;
    if (target == null ? void 0 : target.closest("[data-generation-link]")) return;
    const isMobileVehicleModal = this.usesMobileVehicleModal();
    event.preventDefault();
    if (isMobileVehicleModal) {
      this.openMobileVehicleModal(card);
      return;
    }
    this.toggleVehicleCard(card);
  }
  handleVehicleCardKeydown(event, card) {
    if (card.getAttribute("role") !== "button") return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    if (this.usesMobileVehicleModal()) {
      this.openMobileVehicleModal(card);
      return;
    }
    this.toggleVehicleCard(card);
  }
  openMobileVehicleModal(card) {
    var _a, _b, _c;
    if (!this.mobileModal || !this.mobileModalItems) return;
    if (this.mobileModalCloseTimer) {
      window.clearTimeout(this.mobileModalCloseTimer);
      this.mobileModalCloseTimer = null;
    }
    const vehicleKey = (_a = card.dataset.vehicleKey) == null ? void 0 : _a.trim();
    const linkset = this.getLinkset(this.activeCategoryKey, vehicleKey);
    const items = Array.isArray(linkset == null ? void 0 : linkset.items) ? linkset.items.filter((item) => (item == null ? void 0 : item.label) && (item == null ? void 0 : item.url)) : [];
    if (!items.length) return;
    const categoryLabel = (_c = (_b = this.activeCategoryLabel) == null ? void 0 : _b.textContent) == null ? void 0 : _c.trim();
    const vehicleTitle = card.dataset.vehicleTitle || "";
    const vehicleMake = card.dataset.vehicleMake || "";
    const vehicleBrandLogoSrc = card.dataset.vehicleBrandLogoSrc || "";
    const vehicleBrandLogoAlt = card.dataset.vehicleBrandLogoAlt || vehicleMake || vehicleTitle;
    if (this.mobileModalTitle) {
      this.mobileModalTitle.textContent = vehicleTitle;
    }
    if (this.mobileModalCategory) {
      this.mobileModalCategory.textContent = categoryLabel ? `(${categoryLabel})` : "";
    }
    if (this.mobileModalMake) {
      this.mobileModalMake.textContent = vehicleMake;
      this.mobileModalMake.hidden = !vehicleMake || Boolean(vehicleBrandLogoSrc);
    }
    if (this.mobileModalBrandLogo) {
      if (vehicleBrandLogoSrc) {
        this.mobileModalBrandLogo.src = vehicleBrandLogoSrc;
        this.mobileModalBrandLogo.alt = vehicleBrandLogoAlt;
        this.mobileModalBrandLogo.hidden = false;
      } else {
        this.mobileModalBrandLogo.removeAttribute("src");
        this.mobileModalBrandLogo.alt = "";
        this.mobileModalBrandLogo.hidden = true;
      }
    }
    this.mobileModalItems.innerHTML = items.map((item) => this.renderMobileGenerationItem(item)).join("");
    this.closeVehicleCards();
    this.resetMobileSheetPosition();
    this.mobileModal.classList.remove("hidden");
    this.mobileModal.removeAttribute("hidden");
    card.setAttribute("aria-expanded", "true");
  }
  closeMobileVehicleModal(options = {}) {
    if (!this.mobileModal || this.mobileModal.hasAttribute("hidden")) return;
    const shouldAnimate = Boolean(options.animate);
    this.setMobileCardsExpanded(false);
    if (this.mobileModalCloseTimer) {
      window.clearTimeout(this.mobileModalCloseTimer);
      this.mobileModalCloseTimer = null;
    }
    if (shouldAnimate && this.mobileModalSheet) {
      this.mobileModalSheet.classList.add(
        "translate-y-full",
        "transition-transform",
        "duration-[160ms]",
        "ease-in"
      );
      this.mobileModalCloseTimer = window.setTimeout(() => {
        this.finishMobileModalClose();
      }, 170);
      return;
    }
    this.finishMobileModalClose();
  }
  finishMobileModalClose() {
    if (!this.mobileModal) return;
    this.mobileModal.classList.add("hidden");
    this.mobileModal.setAttribute("hidden", "");
    this.resetMobileSheetPosition();
  }
  setMobileCardsExpanded(isExpanded) {
    this.vehicleCards.forEach((card) => {
      if (card.getAttribute("role") === "button") {
        card.setAttribute("aria-expanded", isExpanded ? "true" : "false");
      }
    });
  }
  resetMobileSheetPosition() {
    if (!this.mobileModalSheet) return;
    this.mobileModalSheet.classList.remove(
      "translate-y-full",
      "transition-transform",
      "duration-[160ms]",
      "ease-in"
    );
  }
  toggleVehicleCard(card) {
    const panel = card.querySelector("[data-vehicle-panel]");
    if (!panel) return;
    const shouldOpen = panel.hidden;
    this.closeVehicleCards(card);
    this.setVehicleCardState(card, shouldOpen);
  }
  closeVehicleCards(exceptCard = null) {
    this.vehicleCards.forEach((card) => {
      if (card === exceptCard) return;
      this.setVehicleCardState(card, false);
    });
  }
  setVehicleCardState(card, isOpen) {
    const panel = card.querySelector("[data-vehicle-panel]");
    const link = card.querySelector("[data-vehicle-link]");
    if (!panel || card.getAttribute("role") !== "button") return;
    panel.hidden = !isOpen;
    card.classList.toggle("is-active", isOpen);
    card.classList.toggle("z-[3]", isOpen);
    card.setAttribute("aria-expanded", isOpen ? "true" : "false");
    if (link && this.usesDesktopCarousel()) {
      link.hidden = isOpen;
    }
  }
  handleDocumentClick(event) {
    const target = event.target instanceof window.Element ? event.target : null;
    const generationLink = target == null ? void 0 : target.closest("[data-generation-link]");
    if (generationLink instanceof window.HTMLAnchorElement) {
      this.handleGenerationLinkClick(event, generationLink);
    }
    if (this.categoryDropdown && this.categoryMenu && !this.categoryMenu.hidden) {
      if (!(target == null ? void 0 : target.closest("[data-category-dropdown]"))) {
        this.setCategoryMenuState(false);
      }
    }
    if (!(target == null ? void 0 : target.closest("[data-vehicle-card]"))) {
      this.closeVehicleCards();
    }
  }
  handleGenerationLinkClick(event, link) {
    const href = link.getAttribute("href");
    if (!href || event.defaultPrevented) {
      return;
    }
    const isPlainLeftClick = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
    const synced = this.syncConvermaxVehicleFromHref(href);
    if (!synced || !isPlainLeftClick) {
      return;
    }
    event.preventDefault();
    window.setTimeout(() => {
      window.location.assign(link.href);
    }, 30);
  }
  handleDocumentKeydown(event) {
    if (event.key === "Escape") {
      this.setCategoryMenuState(false);
      this.closeVehicleCards();
    }
  }
  handleHomeVehicleShopOriginClick(event) {
    const target = event.target instanceof window.Element ? event.target : null;
    const originLink = target == null ? void 0 : target.closest('[data-home-vehicle-shop-origin="true"]');
    if (!originLink) return;
    this.persistHomepageOriginState(originLink.getAttribute("href"));
  }
  persistHomepageOriginState(href) {
    if (!href) return;
    try {
      const targetUrl = new URL(href, window.location.origin);
      window.localStorage.setItem(
        HOMEPAGE_ORIGIN_STORAGE_KEY,
        JSON.stringify({
          pathname: targetUrl.pathname,
          search: targetUrl.search,
          createdAt: Date.now()
        })
      );
    } catch (error) {
    }
  }
  setCategoryMenuState(isOpen) {
    if (!this.categoryMenu || !this.categoryToggle) return;
    this.categoryMenu.hidden = !isOpen;
    this.categoryToggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
    if (this.categoryBackdrop) {
      this.categoryBackdrop.hidden = !isOpen;
    }
  }
  buildPages() {
    if (!this.usesDesktopCarousel()) {
      this.pageStarts = [0];
      this.currentPage = 0;
      this.renderPagination();
      this.updateNavigationVisibility();
      return;
    }
    const maxScrollLeft = this.getMaxScrollLeft();
    const visibleCardCount = this.getVisibleCardCount();
    const pageCount = maxScrollLeft > 1 ? Math.min(this.cards.length, visibleCardCount) : 1;
    const pageStarts = Array.from({ length: pageCount }, (_, i) => i);
    this.pageStarts = [...new Set(pageStarts)].sort((left, right) => left - right);
    this.currentPage = Math.min(this.currentPage, Math.max(this.pageStarts.length - 1, 0));
    this.renderPagination();
    this.updateNavigationVisibility();
  }
  renderPagination() {
    if (!this.pagination) return;
    this.pagination.innerHTML = "";
    const dotCount = this.getPaginationDotCount();
    Array.from({ length: dotCount }).forEach((_, dotIndex) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "h-[4px] w-10 rounded-[1px] bg-[#D9D9D980] transition duration-300 aria-[current=true]:bg-[#E0E0E0]";
      button.setAttribute("data-page-dot", "");
      button.addEventListener("click", () => {
        this.goToPage(this.getPageIndexForDot(dotIndex));
      });
      this.pagination.appendChild(button);
    });
    this.pageDots = Array.from(this.pagination.querySelectorAll("[data-page-dot]"));
  }
  getPaginationDotCount() {
    var _a;
    if (!((_a = this.pageStarts) == null ? void 0 : _a.length)) return 0;
    return this.pageStarts.length;
  }
  getVisibleCardCount() {
    if (!this.usesDesktopCarousel()) return this.cards.length || 1;
    if (this.cards.length < 2) return 1;
    const firstOffset = this.getCardOffset(0);
    const secondOffset = this.getCardOffset(1);
    const step = Math.max(1, secondOffset - firstOffset);
    const visibleWidth = Math.max(1, this.scroller.clientWidth - this.leadingPadding);
    return Math.max(1, Math.floor(visibleWidth / step));
  }
  getCardOffset(index) {
    const card = this.cards[index];
    if (!card) return 0;
    const cardRect = card.getBoundingClientRect();
    const scrollerRect = this.scroller.getBoundingClientRect();
    const cardOffset = cardRect.left - scrollerRect.left + this.scroller.scrollLeft;
    return Math.max(0, cardOffset - this.leadingPadding);
  }
  goToPage(pageIndex) {
    var _a;
    if (!((_a = this.pageStarts) == null ? void 0 : _a.length) || !this.usesDesktopCarousel()) return;
    this.currentPage = Math.max(0, Math.min(pageIndex, this.pageStarts.length - 1));
    this.isProgrammaticScroll = true;
    if (this.goToPageTimer) {
      window.clearTimeout(this.goToPageTimer);
    }
    this.goToPageTimer = window.setTimeout(() => {
      this.isProgrammaticScroll = false;
      this.goToPageTimer = null;
    }, 500);
    this.scroller.scrollTo({
      left: this.getPageScrollLeft(this.currentPage),
      behavior: "smooth"
    });
    this.updateButtons();
    this.updatePagination();
  }
  updateButtons() {
    const atStart = this.currentPage === 0;
    const atEnd = this.currentPage === this.pageStarts.length - 1;
    this.prevButtons.forEach((button) => {
      button.disabled = atStart;
    });
    this.nextButtons.forEach((button) => {
      button.disabled = atEnd;
    });
  }
  updatePagination() {
    var _a;
    const activeDotIndex = this.getActiveDotIndex();
    (_a = this.pageDots) == null ? void 0 : _a.forEach((dot, dotIndex) => {
      const pageIndex = this.getPageIndexForDot(dotIndex);
      dot.dataset.pageIndex = String(pageIndex);
      dot.setAttribute("aria-label", `Go to vehicle page ${pageIndex + 1}`);
      dot.setAttribute("aria-current", dotIndex === activeDotIndex ? "true" : "false");
    });
  }
  getActiveDotIndex() {
    var _a;
    if (!((_a = this.pageStarts) == null ? void 0 : _a.length)) return 0;
    return this.currentPage;
  }
  getPageIndexForDot(dotIndex) {
    var _a;
    if (!((_a = this.pageStarts) == null ? void 0 : _a.length)) return 0;
    return Math.max(0, Math.min(dotIndex, this.pageStarts.length - 1));
  }
  updateNavigationVisibility() {
    const shouldShowNavigation = this.usesDesktopCarousel() && this.pageStarts.length > 1;
    this.navShells.forEach((shell) => {
      shell.classList.toggle("hidden", !shouldShowNavigation);
    });
    if (this.pagination) {
      this.pagination.classList.toggle("hidden", !shouldShowNavigation);
    }
  }
  handleScroll() {
    if (!this.usesDesktopCarousel()) return;
    if (this.isProgrammaticScroll) return;
    if (this.scrollFrame) {
      window.cancelAnimationFrame(this.scrollFrame);
    }
    this.scrollFrame = window.requestAnimationFrame(() => {
      this.currentPage = this.getNearestPageIndex();
      this.updateButtons();
      this.updatePagination();
      this.scrollFrame = null;
    });
  }
  handleResize() {
    this.updateScrollerPadding();
    if (this.usesDesktopCarousel()) {
      this.closeMobileVehicleModal();
    }
    this.buildPages();
    this.currentPage = this.usesDesktopCarousel() ? this.getNearestPageIndex() : 0;
    this.updateButtons();
    this.updatePagination();
  }
  updateScrollerPadding() {
    const computedStyle = window.getComputedStyle(this.scroller);
    this.leadingPadding = parseFloat(computedStyle.paddingLeft) || 0;
  }
  getMaxScrollLeft() {
    return Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth);
  }
  getPageScrollLeft(pageIndex) {
    var _a;
    if (!((_a = this.pageStarts) == null ? void 0 : _a.length)) return 0;
    const maxScrollLeft = this.getMaxScrollLeft();
    if (this.pageStarts.length <= 1 || maxScrollLeft <= 1) return 0;
    const clampedPageIndex = Math.max(0, Math.min(pageIndex, this.pageStarts.length - 1));
    return clampedPageIndex / (this.pageStarts.length - 1) * maxScrollLeft;
  }
  getNearestPageIndex() {
    var _a;
    if (!((_a = this.pageStarts) == null ? void 0 : _a.length) || !this.usesDesktopCarousel()) return 0;
    let closestPageIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    this.pageStarts.forEach((_, pageIndex) => {
      const distance = Math.abs(this.scroller.scrollLeft - this.getPageScrollLeft(pageIndex));
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPageIndex = pageIndex;
      }
    });
    return closestPageIndex;
  }
}
if (!window.customElements.get("home-vehicle-shop")) {
  window.customElements.define("home-vehicle-shop", HomeVehicleShop);
}
